import { useEffect, useState } from "react";
import { Plus, Trash2, Upload, X } from "lucide-react";
import { PROFESSION_CATEGORY_LABELS, type ProfessionCategory, type ProfessionCatalogEntry } from "../data/professionalTaxonomy";
import { ImageLightbox } from "./ImageLightbox";
import { type CredentialDraft, type Draft, MAX_CREDENTIALS_PER_REQUEST, MAX_EVIDENCE_IMAGES, MAX_EVIDENCE_BYTES_PER_IMAGE, ALLOWED_EVIDENCE_TYPES, proofSuggestions, issuerSuggestions, findProfession, qualificationPathsFor } from "./professionalVerificationModel";

export function SubmissionDraftCard({
  draft,
  submitting,
  onChangeTitle,
  onChangeCredential,
  onAddEvidence,
  onAddCredential,
  onRemoveCredential,
  onRemoveDraft,
}: {
  draft: Draft;
  submitting: boolean;
  onChangeTitle: (id: string, update: Partial<Draft>) => void;
  onChangeCredential: (draftId: string, credentialId: string, update: Partial<CredentialDraft>) => void;
  onAddEvidence: (draftId: string, credentialId: string, files: File[]) => void;
  onAddCredential: (draftId: string) => void;
  onRemoveCredential: (draftId: string, credentialId: string) => void;
  onRemoveDraft: (id: string) => void;
}) {
  const relation = draft.mode === "correction" ? "Correct and resubmit rejected request"
    : draft.mode === "addition" ? "Add credentials to approved profession"
      : "New profession request";
  return (
    <article className="rounded-xl border border-line bg-white p-5 shadow-sm sm:p-6" aria-label={`${draft.professionTitle} evidence form`}>
      <div className="flex items-start gap-3 border-b border-line pb-4">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-navy">{draft.professionTitle}</div>
          {draft.mode !== "new" && <p className="text-xs text-mut mt-0.5">{relation}</p>}
        </div>
        <button type="button" aria-label={`Remove ${draft.professionTitle}`} onClick={() => onRemoveDraft(draft.id)} disabled={submitting} className="btn-ghost h-8 w-8 p-0 text-brand-red disabled:opacity-50">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {draft.mode === "correction" && (
        <div className="mt-3">
          <label className="label" htmlFor={`corrected-title-${draft.id}`}>Profession title</label>
          <input
            id={`corrected-title-${draft.id}`}
            className="field mt-1"
            value={draft.professionTitle}
            maxLength={160}
            required
            disabled={submitting}
            onChange={(event) => {
              const professionTitle = event.target.value;
              onChangeTitle(draft.id, { professionTitle, entry: findProfession(professionTitle) });
            }}
          />
        </div>
      )}
      <div className="space-y-5">
        {draft.credentials.map((credential, index) => (
          <CredentialFields
            key={credential.id}
            entry={draft.entry}
            credential={credential}
            credentialNumber={index + 1}
            credentialCount={draft.credentials.length}
            submitting={submitting}
            onChange={(update) => onChangeCredential(draft.id, credential.id, update)}
            onAddEvidence={(files) => onAddEvidence(draft.id, credential.id, files)}
            onRemove={() => onRemoveCredential(draft.id, credential.id)}
          />
        ))}
        <button type="button" onClick={() => onAddCredential(draft.id)} disabled={submitting || draft.credentials.length >= MAX_CREDENTIALS_PER_REQUEST} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> Add another credential
        </button>
      </div>
    </article>
  );
}

function CredentialFields({ entry, credential, credentialNumber, credentialCount, submitting, onChange, onAddEvidence, onRemove }: {
  entry: ProfessionCatalogEntry | null;
  credential: CredentialDraft;
  credentialNumber: number;
  credentialCount: number;
  submitting: boolean;
  onChange: (update: Partial<CredentialDraft>) => void;
  onAddEvidence: (files: File[]) => void;
  onRemove: () => void;
}) {
  const paths = qualificationPathsFor(entry);
  const evidenceLimitReached = credential.evidence.length >= MAX_EVIDENCE_IMAGES;
  const suggestedProofs = proofSuggestions(entry, credential.qualificationPath);
  const suggestedIssuers = issuerSuggestions(entry, credential.qualificationPath, credential.credentialType);
  function textField(field: "credentialType" | "credentialNumber" | "issuer", label: string, required: boolean, maxLength: number, placeholder: string) {
    const value = credential[field];
    const inputId = `${field}-${credential.id}`;
    return (
      <div>
        <label className="label" htmlFor={inputId}>{label}{required ? " *" : ""}</label>
        <input id={inputId} className="field mt-1" value={value} maxLength={maxLength} required={required} disabled={submitting} placeholder={placeholder} onChange={(event) => onChange({ [field]: event.target.value })} />
      </div>
    );
  }
  return (
    <div className={`space-y-5 pt-5 ${credentialCount > 1 ? "border-b border-line pb-5 last:border-b-0" : ""}`} role={credentialCount > 1 ? "group" : undefined} aria-label={credentialCount > 1 ? `Credential ${credentialNumber}` : undefined}>
      {credentialCount > 1 && <h3 className="text-sm font-semibold text-navy">Credential {credentialNumber}</h3>}
      <div>
        <label className="label" htmlFor={`classification-${credential.id}`}>Qualification path *</label>
        <select
          id={`classification-${credential.id}`}
          className="field mt-1"
          value={credential.qualificationPath ?? ""}
          onChange={(event) => onChange({ qualificationPath: event.target.value as ProfessionCategory, credentialType: "", issuer: "" })}
          disabled={submitting || Boolean(entry && paths.length === 1)}
        >
          {paths.length !== 1 && <option value="" disabled>Select a qualification path</option>}
          {paths.map((path) => <option key={path} value={path}>{PROFESSION_CATEGORY_LABELS[path]}</option>)}
        </select>
        {entry && paths.length === 1 && <p className="mt-1 text-xs text-mut">This profession has one path in the current catalog.</p>}
      </div>
      <div className="grid gap-5 sm:grid-cols-2 sm:gap-4">
        <div>
          <label className="label" htmlFor={`credentialType-${credential.id}`}>What proof do you have? *</label>
          <input id={`credentialType-${credential.id}`} className="field mt-1" list={`proof-suggestions-${credential.id}`} value={credential.credentialType} maxLength={120} required disabled={submitting || !credential.qualificationPath} placeholder="Choose a suggestion or type your proof" onChange={(event) => onChange({ credentialType: event.target.value, issuer: "" })} />
          <datalist id={`proof-suggestions-${credential.id}`}>{suggestedProofs.map((suggestion) => <option key={suggestion} value={suggestion} />)}</datalist>
        </div>
        <div>
          <label className="label" htmlFor={`issuer-${credential.id}`}>Who issued or can confirm it? *</label>
          <input id={`issuer-${credential.id}`} className="field mt-1" list={`issuer-suggestions-${credential.id}`} value={credential.issuer} maxLength={160} required disabled={submitting || !credential.qualificationPath} placeholder="Choose a suggestion or type an issuer" onChange={(event) => onChange({ issuer: event.target.value })} />
          <datalist id={`issuer-suggestions-${credential.id}`}>{suggestedIssuers.map((suggestion) => <option key={suggestion} value={suggestion} />)}</datalist>
        </div>
      </div>
      <div>
        <span className="label">Upload proof * (1–3 images)</span>
        <input
          id={`evidence-${credential.id}`}
          className="sr-only peer"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          disabled={submitting || evidenceLimitReached}
          onChange={(event) => {
            onAddEvidence(Array.from(event.target.files ?? []));
            event.currentTarget.value = "";
          }}
        />
        <label
          htmlFor={`evidence-${credential.id}`}
          className={`mt-2 flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-navy-200 bg-navy-50/30 px-4 py-6 text-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-navy-500 ${submitting || evidenceLimitReached ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-navy-400 hover:bg-navy-50"}`}
          aria-disabled={submitting || evidenceLimitReached}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (!submitting && !evidenceLimitReached) onAddEvidence(Array.from(event.dataTransfer.files));
          }}
        >
          <Upload className="h-6 w-6 text-navy-500" aria-hidden="true" />
          <span className="font-medium text-navy">{evidenceLimitReached ? "Maximum 3 images selected" : "Choose images or drag them here"}</span>
          {!evidenceLimitReached && <span className="rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-semibold text-navy">Browse files</span>}
          <span className="text-xs text-mut">JPEG, PNG, or WebP · 10 MiB each · 30 MiB per request</span>
        </label>
        <SelectedEvidenceGallery
          files={credential.evidence}
          disabled={submitting}
          onRemove={(index) => onChange({ evidence: credential.evidence.filter((_, fileIndex) => fileIndex !== index) })}
        />
        {credential.evidence.length > MAX_EVIDENCE_IMAGES && <p role="alert" className="mt-1 text-xs text-brand-red">Choose up to {MAX_EVIDENCE_IMAGES} images.</p>}
        {credential.evidence.some((file) => !ALLOWED_EVIDENCE_TYPES.has(file.type) || file.size === 0 || file.size > MAX_EVIDENCE_BYTES_PER_IMAGE) && <p role="alert" className="mt-1 text-xs text-brand-red">Use JPEG, PNG, or WebP images under 10 MiB each.</p>}
        <p className="mt-1 text-xs text-mut">Only you and authorized reviewers can see these images.</p>
      </div>
      <div className="border-t border-line pt-4 text-sm">
        <h4 className="font-medium text-navy">Optional details</h4>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {textField("credentialNumber", "Credential number", false, 80, "Optional")}
          <div>
            <label className="label" htmlFor={`expiryDate-${credential.id}`}>Expiry date</label>
            <input id={`expiryDate-${credential.id}`} className="field mt-1" type="date" value={credential.expiryDate} disabled={submitting} onChange={(event) => onChange({ expiryDate: event.target.value })} />
          </div>
        </div>
      </div>
      {credentialCount > 1 && <button type="button" onClick={onRemove} disabled={submitting} className="btn-ghost h-8 px-2 text-xs text-brand-red disabled:opacity-50">Remove credential</button>}
    </div>
  );
}

function SelectedEvidenceGallery({ files, disabled, onRemove }: { files: File[]; disabled: boolean; onRemove: (index: number) => void }) {
  const [previews, setPreviews] = useState<{ file: File; url: string }[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    const nextPreviews = files.map((file) => ({ file, url: URL.createObjectURL(file) }));
    setPreviews(nextPreviews);
    return () => nextPreviews.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [files]);

  if (files.length === 0) return null;
  const visiblePreviews = files.map((file) => previews.find((preview) => preview.file === file)).filter((preview): preview is { file: File; url: string } => !!preview);

  return (
    <>
      <ul className="mt-3 flex flex-wrap gap-2" aria-label="Selected proof images">
        {visiblePreviews.map(({ file, url }, index) => (
          <li key={url} className="relative h-20 w-24 overflow-hidden rounded-lg border border-line bg-navy-50 sm:h-24 sm:w-28">
            <button type="button" onClick={() => setActiveIndex(index)} aria-label={`Preview ${file.name}`} className="h-full w-full cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
              <img src={url} alt={file.name} className="h-full w-full object-cover" />
            </button>
            <button type="button" onClick={() => { setActiveIndex(null); onRemove(index); }} aria-label={`Remove ${file.name}`} disabled={disabled} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/70 text-white hover:bg-black/85 disabled:opacity-50">
              <X className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
      {activeIndex !== null && visiblePreviews[activeIndex] && (
        <ImageLightbox
          images={visiblePreviews.map(({ file, url }) => ({ src: url, alt: file.name }))}
          initialIndex={activeIndex}
          onClose={() => setActiveIndex(null)}
        />
      )}
    </>
  );
}
